import React, { useEffect, useMemo, useRef, useState } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-toastify';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../supabaseClient';
import posthog from '../../posthogClient';
import { setWorkspaceLoaded } from '../../store/workspacesSlice';
import { getBrowserTimeZone } from '../../lib/browserTimeZone';

const C = {
  primary: '#8B4513',
  bg: '#FDF6F0',
  border: '2px solid #111',
  thin: '1px solid #ddd',
  pastel: {
    yellow: '#FFFBCC',
    blue: '#E8F4FD',
    green: '#E8F5E9',
    orange: '#FFF3E0',
    teal: '#E0F2F1',
    pink: '#FCE4EC',
    purple: '#F3E5F5',
    lavender: '#EDE7F6',
  },
};

const VD = 'Verdana, Geneva, sans-serif';

const STEP_NAMES = ['beta_mark_hard', 'basket_named', 'simultaneous_revision', 'optic_add_to_basket', 'congrats', 'strong_basket', 'study_mode'];

const captureOnboardingEvent = (eventName, properties = {}) => {
  if (!process.env.REACT_APP_POSTHOG_KEY) return;
  try {
    posthog.capture(eventName, properties);
  } catch (e) {
    // Avoid breaking onboarding on analytics failures
    console.error('PostHog capture error:', e);
  }
};

const syncBrevoContact = () => {
  void supabase.auth
    .getSession()
    .then(({ data: { session } }) => {
      if (!session?.access_token) return;

      return fetch('/api/brevo-contact', {
        method: 'POST',
        keepalive: true,
        headers: {
          Authorization: `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ action: 'sync', timezone: getBrowserTimeZone() }),
      }).then((response) => {
        if (!response.ok) {
          console.warn('Brevo onboarding sync failed:', response.status);
        }
      });
    })
    .catch((error) => {
      console.warn('Brevo onboarding sync error:', error);
    });
};

const BETA_FACTS = [
  { id: 'asthma', text: 'Contraindicated in asthma / COPD due to bronchoconstriction', basket: 'bronchoconstrict' },
  { id: 'blocks', text: 'Contraindicated in heart block and severe bradycardia', basket: 'bradycardia' },
  { id: 'sleep', text: 'Causes sleep disturbances and vivid dreams (lipophilic agents)', basket: 'sleep disturbance' },
  { id: 'hypogly', text: 'Masks hypoglycemia symptoms, except sweating', basket: 'mask' },
  { id: 'rebound', text: 'Abrupt withdrawal triggers rebound hypertension and angina', basket: 'rebound' },
];

const OPTIC_FACTS = [
  { id: 'pain', text: 'Painful acute vision loss', basket: 'painful' },
  { id: 'rapd', text: 'Marcus Gunn pupil (RAPD)', basket: 'marcus gunn' },
  { id: 'ms', text: 'Most common initial presentation of MS', basket: 'MS' },
  { id: 'uhthoff', text: "Uhthoff's phenomenon: worsens with heat", basket: 'heat' },
  { id: 'tx', text: 'IV methylprednisolone', basket: 'methylprednisolone' },
];

const TERMINAL_ILEUM_BASKET = {
  name: 'terminal ileum',
  topics: [
    { name: 'Vitamin B12', order: 'first', reason: 'gives the basket its name', detail: 'Absorbed in the terminal ileum', color: C.pastel.yellow, tag: 'Gastroenterology' },
    { name: 'STEMI', order: 'second', reason: '"12" in B12 → 12-hr fibrinolysis window', detail: 'Fibrinolysis within 12 hrs', color: C.pastel.blue, tag: 'Cardiology' },
    { name: 'Marfan Syndrome', order: 'third', reason: 'upward lens dislocation ↔ upward ST in STEMI', detail: 'Upward lens dislocation', color: C.pastel.green, tag: 'Genetics' },
  ],
};

const detailKeyword = (text) => {
  if (!text) return '';
  const stop = new Set(['with', 'the', 'a', 'an', 'of', 'in', 'on', 'and', 'or', 'is', 'are', 'for', 'to', 'its', 'by', 'most', 'common', 'initial', 'loss', 'acute', 'except']);
  const words = text.replace(/[().,;>—–]/g, '').split(' ');
  const candidates = words.filter((w) => w.length > 3 && !stop.has(w.toLowerCase()));
  return (candidates[candidates.length - 1] || words[0]).toLowerCase();
};

const formatRelationDescriptionBeta = ({ topicName, basketName }) => {
  const tn = (topicName || '').trim();
  const bn = (basketName || '').trim();
  if (!tn && !bn) return '';
  if (!tn) return bn;
  if (!bn) return tn;
  return `${tn} ${bn}`;
};

const formatRelationDescriptionOptic = ({ topicName, basketName }) => {
  const tn = (topicName || '').trim();
  const bn = (basketName || '').trim();
  if (!tn && !bn) return '';
  if (!tn) return bn;
  if (!bn) return tn;
  return `${tn} is ${bn}`;
};

const getBetaHardcodedRelationDescription = (hardText) => {
  const text = (hardText || '').trim();
  if (!text) return '';

  if (text === 'Contraindicated in asthma / COPD due to bronchoconstriction') return 'cause bronchoconstriction';
  if (text === 'Contraindicated in heart block and severe bradycardia') return 'cause bradycardia';
  if (text === 'Causes sleep disturbances and vivid dreams (lipophilic agents)') return 'cause sleep disturbance';
  if (text === 'Masks hypoglycemia symptoms, except sweating') return 'mask hypoglycemia';
  if (text === 'Abrupt withdrawal triggers rebound hypertension and angina') return 'cause rebound htn and angina';

  return '';
};

const getOpticHardcodedRelationDescription = (hardOpticText) => {
  const text = (hardOpticText || '').trim();
  if (!text) return '';

  if (text === 'Painful acute vision loss') return 'optic neuritis is painful';
  if (text === 'Marcus Gunn pupil (RAPD)') return 'demonstrates marcus gunn';
  if (text === 'Most common initial presentation of MS') return 'mc initial presentation of MS';
  if (text === "Uhthoff's phenomenon: worsens with heat") return 'worsens with heat';
  if (text === 'IV methylprednisolone') return 'treatment is methylprednisolone';

  return '';
};

const getTerminalIleumHardcodedRelationDescription = (topicName) => {
  const name = (topicName || '').trim();
  if (!name) return '';

  if (name === 'Vitamin B12') return 'absorbed in terminal ileum';
  if (name === 'STEMI') return '"12" in B12 → 12-hr fibrinolysis window';
  if (name === 'Marfan Syndrome') return 'upward lens dislocation ↔ upward ST in STEMI';

  return '';
};

const BtnPrimary = ({ children, onClick, disabled = false, style = {} }) => {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        width: '100%',
        padding: '13px 20px',
        background: disabled ? '#ccc' : C.primary,
        color: '#fff',
        border: C.border,
        borderRadius: 8,
        fontFamily: VD,
        fontWeight: 'bold',
        fontSize: 14,
        cursor: disabled ? 'not-allowed' : 'pointer',
        lineHeight: 1.4,
        ...style,
      }}
    >
      {children}
    </button>
  );
};

const Pill = ({ children, bg = '#fff', color = '#111', style = {} }) => {
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        background: bg,
        border: C.border,
        borderRadius: 4,
        padding: '2px 6px',
        fontSize: 10,
        fontFamily: VD,
        fontWeight: '700',
        color,
        whiteSpace: 'nowrap',
        ...style,
      }}
    >
      {children}
    </span>
  );
};

const ColLabel = ({ children }) => {
  return (
    <div
      style={{
        fontSize: 10,
        color: '#aaa',
        fontFamily: VD,
        textTransform: 'uppercase',
        letterSpacing: 1,
        marginBottom: 4,
      }}
    >
      {children}
    </div>
  );
};

const BasketCard = ({ name, topics, badge, selected = false, style = {} }) => {
  return (
    <div
      style={{
        border: selected ? `2px solid ${C.primary}` : C.border,
        borderRadius: 9,
        overflow: 'hidden',
        background: '#fff',
        boxShadow: selected ? `2px 2px 0 ${C.primary}` : '2px 2px 0 #111',
        ...style,
      }}
    >
      <div
        style={{
          background: selected ? C.bg : '#fafafa',
          borderBottom: C.thin,
          padding: '7px 9px',
          display: 'flex',
          alignItems: 'center',
          gap: 5,
        }}
      >
        <span style={{ fontSize: 13, flexShrink: 0 }}>🧺</span>
        <span
          style={{
            fontFamily: VD,
            fontWeight: 'bold',
            fontSize: 12,
            color: selected ? C.primary : '#444',
            flex: 1,
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {name}
        </span>
        {badge && <Pill bg={C.pastel.green} color="#2e7d32" style={{ fontSize: 9 }}>{badge}</Pill>}
        <span style={{ fontSize: 10, color: '#bbb', flexShrink: 0 }}>{topics.length}t</span>
      </div>
      {topics.map((t, i) => (
        <div
          key={`${t.name}-${i}`}
          style={{
            background: '#fff',
            borderBottom: i < topics.length - 1 ? C.thin : 'none',
            padding: '8px 9px 8px 9px',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
            <div
              style={{
                display: 'inline-block',
                background: t.color,
                borderRadius: 5,
                padding: '2px 8px',
                fontWeight: '700',
                fontSize: 11,
                color: '#111',
                fontFamily: VD,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                maxWidth: t.order ? '70%' : '100%',
              }}
            >
              {t.name}
            </div>
            {t.order && (
              <span style={{ fontSize: 9, color: '#bbb', fontFamily: VD, whiteSpace: 'nowrap', flexShrink: 0 }}>
                added {t.order}
              </span>
            )}
          </div>
          {t.reason && (
            <div
              style={{
                fontSize: 9,
                color: C.primary,
                fontFamily: VD,
                fontStyle: 'italic',
                lineHeight: 1.3,
                opacity: 0.85,
                marginBottom: 2,
              }}
            >
              🔗 {t.reason}
            </div>
          )}
          <div style={{ fontSize: 10, color: '#666', fontFamily: VD, lineHeight: 1.4 }}>{t.detail}</div>
        </div>
      ))}
    </div>
  );
};

const HardBadge = ({ animate = false }) => {
  return (
    <span
      style={{
        background: C.primary,
        color: '#fff',
        border: `2px solid ${C.primary}`,
        borderRadius: 4,
        padding: '2px 7px',
        fontSize: 9,
        fontFamily: VD,
        fontWeight: 'bold',
        flexShrink: 0,
        display: 'inline-block',
        transform: animate ? 'scale(1.35)' : 'scale(1)',
        transition: 'transform .3s cubic-bezier(.34,1.56,.64,1)',
      }}
    >
      Hard
    </span>
  );
};

const MarkAsHardBtn = ({ onClick, dimmed }) => {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        background: 'none',
        color: '#bbb',
        border: '1px solid #ddd',
        borderRadius: 4,
        padding: '2px 6px',
        fontSize: 9,
        fontFamily: VD,
        fontWeight: 'bold',
        flexShrink: 0,
        cursor: onClick ? 'pointer' : 'default',
        opacity: dimmed ? 0.25 : 1,
        transition: 'opacity .2s',
      }}
    >
      Mark as hard
    </button>
  );
};

const TopicCard = ({ title, subject, color, facts, hardId, onMark }) => {
  return (
    <div style={{ border: C.border, borderRadius: 10, overflow: 'hidden', background: '#fff', boxShadow: '2px 2px 0 #111' }}>
      <div style={{ background: color, borderBottom: C.border, padding: '8px 10px', display: 'flex', alignItems: 'center', gap: 6 }}>
        <span style={{ fontFamily: VD, fontWeight: 'bold', fontSize: 12, color: '#111' }}>{title}</span>
        <Pill>{subject}</Pill>
      </div>
      {facts.map((f, i) => {
        const isHard = hardId === f.id;
        return (
          <div
            key={f.id}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '7px 10px',
              borderBottom: i < facts.length - 1 ? C.thin : 'none',
              background: isHard ? C.pastel.orange : '#fff',
              transition: 'background .2s',
            }}
          >
            <span
              style={{
                fontSize: 11,
                fontFamily: VD,
                color: '#333',
                flex: 1,
                paddingRight: 6,
                lineHeight: 1.4,
                fontWeight: isHard ? 'bold' : 'normal',
              }}
            >
              {f.text}
            </span>
            {onMark ? (
              isHard ? (
                <HardBadge animate />
              ) : (
                <MarkAsHardBtn onClick={() => onMark(f.id)} dimmed={!!hardId && !isHard} />
              )
            ) : isHard ? (
              <HardBadge />
            ) : (
              <MarkAsHardBtn dimmed={!!hardId} />
            )}
          </div>
        );
      })}
    </div>
  );
};

const SearchBar = ({ value, onChange, searchRef, placeholder = 'search baskets...' }) => {
  return (
    <div style={{ position: 'relative' }}>
      <span style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', fontSize: 11, pointerEvents: 'none' }}>🔎</span>
      <input
        ref={searchRef}
        value={value}
        onChange={(e) => (onChange ? onChange(e.target.value) : undefined)}
        placeholder={placeholder}
        readOnly={!onChange}
        style={{
          width: '100%',
          boxSizing: 'border-box',
          padding: '7px 7px 7px 24px',
          border: C.border,
          borderRadius: 7,
          fontFamily: VD,
          fontSize: 11,
          color: '#333',
          outline: 'none',
          background: '#fff',
        }}
      />
    </div>
  );
};

const Step1MarkHard = ({ onNext, onHardMarked }) => {
  const [hardId, setHardId] = useState(null);
  const [celebrated, setCelebrated] = useState(false);

  const handleMark = (fid) => {
    if (onHardMarked) {
      const fact = BETA_FACTS.find((f) => f.id === fid);
      onHardMarked({
        hard_id: fid,
        hard_text: fact?.text,
        basket_name: fact?.basket ?? detailKeyword(fact?.text),
      });
    }
    setHardId(fid);
    setTimeout(() => setCelebrated(true), 350);
  };

  const hardFact = BETA_FACTS.find((f) => f.id === hardId);
  const hardText = hardFact?.text;
  const basketName = hardFact?.basket ?? (hardId ? detailKeyword(hardText) : null);

  return (
    <div>
      <p style={{ fontSize: 13, color: '#666', textAlign: 'center', marginBottom: 16, fontFamily: VD, lineHeight: 1.6 }}>
        You're studying <strong>Beta Blockers</strong>.<br />
        Which detail is hardest for you to remember?
      </p>

      <TopicCard title="Beta Blockers" subject="Pharmacology" color={C.pastel.teal} facts={BETA_FACTS} hardId={hardId} onMark={!hardId ? handleMark : null} />

      <div
        style={{
          marginTop: 14,
          opacity: celebrated ? 1 : 0,
          transform: celebrated ? 'translateY(0)' : 'translateY(6px)',
          transition: 'opacity .3s, transform .3s',
          pointerEvents: celebrated ? 'auto' : 'none',
        }}
      >
        <div style={{ background: C.pastel.orange, border: C.border, borderRadius: 8, padding: '11px 14px', fontSize: 12, fontFamily: VD, color: '#bf360c', lineHeight: 1.6, marginBottom: 14 }}>
          Good. That's the one we'll build around. We'll name the basket after this detail.{' '}
          <span style={{ color: '#e07040', fontStyle: 'italic' }}>
            If you find other details hard too, don't worry. Once you've mastered this one, you can come back and mark the next.
          </span>
        </div>
        <BtnPrimary onClick={() => onNext({ hardId, hardText, basketName })}>See what happens next →</BtnPrimary>
      </div>
    </div>
  );
};

const Step2BasketNamed = ({ hardText, basketName, onNext }) => {
  const betaTopics = [{ name: 'Beta Blockers', detail: hardText, color: C.pastel.teal, tag: 'Pharmacology' }];
  return (
    <div>
      <h2 style={{ fontSize: 20, fontWeight: 'bold', textAlign: 'center', marginBottom: 6, color: '#111', fontFamily: VD }}>
        We named the basket after your hard detail
      </h2>

      <div style={{ background: C.pastel.blue, border: C.border, borderRadius: 8, padding: '11px 14px', fontSize: 12, fontFamily: VD, color: '#1a237e', lineHeight: 1.7, marginBottom: 12 }}>
        By marking this as the hard detail, you've made it the defining characteristic of this topic in your memory. It's no longer buried among facts you already know.
      </div>

      <div style={{ padding: '0 12px', marginBottom: 12 }}>
        <BasketCard name={basketName} topics={betaTopics} selected />
      </div>

      <div style={{ background: C.pastel.green, border: C.border, borderRadius: 8, padding: '11px 14px', fontSize: 12, fontFamily: VD, color: '#1b5e20', lineHeight: 1.7, marginBottom: 20 }}>
        With repetition, this detail will stop feeling hard. That's the point. Once it's no longer a struggle, you mark the next one.
      </div>

      <BtnPrimary onClick={onNext}>What happens when I move to the next subject? →</BtnPrimary>
    </div>
  );
};

const Step3BSIPreview = ({ basketName, hardText, onNext }) => {
  const betaTopics = [{ name: 'Beta Blockers', detail: hardText, color: C.pastel.teal, tag: 'Pharmacology' }];

  const templateFacts = [
    { text: 'First fact about this topic' },
    { text: 'Second fact — this is the hard one', hard: true },
    { text: 'Third fact about this topic' },
  ];

  return (
    <div>
      <div style={{ textAlign: 'center', marginBottom: 16 }}>
        <h2 style={{ fontSize: 20, fontWeight: 'bold', color: '#111', fontFamily: VD, marginBottom: 6 }}>Simultaneous revision</h2>
        <p style={{ fontSize: 13, color: '#666', fontFamily: VD, lineHeight: 1.6 }}>Your new item is on the left. Every basket you've built is on the right.</p>
      </div>

      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', marginBottom: 12 }}>
        <div style={{ flex: '0 0 46%', display: 'flex', flexDirection: 'column', gap: 8 }}>
          <ColLabel>New item</ColLabel>
          <div style={{ border: C.border, borderRadius: 10, overflow: 'hidden', background: '#fff', boxShadow: '2px 2px 0 #111' }}>
            <div style={{ background: C.pastel.yellow, borderBottom: C.border, padding: '8px 10px', display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontFamily: VD, fontWeight: 'bold', fontSize: 12, color: '#888', fontStyle: 'italic' }}>Your 2nd topic</span>
            </div>
            {templateFacts.map((f, i) => (
              <div
                key={i}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '7px 10px',
                  borderBottom: i < templateFacts.length - 1 ? C.thin : 'none',
                  background: f.hard ? C.pastel.orange : '#fff',
                }}
              >
                <span style={{ fontSize: 11, fontFamily: VD, color: f.hard ? '#333' : '#bbb', flex: 1, paddingRight: 6, fontWeight: f.hard ? 'bold' : 'normal', fontStyle: !f.hard ? 'italic' : 'normal' }}>
                  {f.text}
                </span>
                {f.hard ? <HardBadge /> : <MarkAsHardBtn dimmed />}
              </div>
            ))}
          </div>
          <SearchBar value="" placeholder="search your baskets..." />
        </div>

        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
          <ColLabel>Your baskets</ColLabel>
          <BasketCard name={basketName} topics={betaTopics} />
        </div>
      </div>

      <div style={{ background: C.pastel.lavender, border: C.border, borderRadius: 8, padding: '11px 14px', fontSize: 12, fontFamily: VD, color: '#4527a0', lineHeight: 1.7, marginBottom: 16 }}>
        Every time you move to a new item, your old items are on the right with their hard details visible. Scanning them while studying something new means you quietly revisit everything you've built — including topics you covered months ago.
      </div>

      <BtnPrimary onClick={onNext}>Let's move to an actual item →</BtnPrimary>
    </div>
  );
};

const Step4OpticBasket = ({ basketName, hardText, onNext, onHardMarked }) => {
  const [hardId, setHardId] = useState(null);
  const [searchVal, setSearchVal] = useState('');
  const [newBasketMode, setNewBasketMode] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [opticBasket, setOpticBasket] = useState('');
  const searchRef = useRef(null);

  const betaTopics = [{ name: 'Beta Blockers', detail: hardText, color: C.pastel.teal, tag: 'Pharmacology' }];
  const myBaskets = [{ id: 'beta', name: basketName, topics: betaTopics }];

  const filtered = searchVal.trim() ? myBaskets.filter((b) => b.name.toLowerCase().includes(searchVal.toLowerCase())) : myBaskets;
  const noMatch = searchVal.trim().length > 0 && filtered.length === 0;
  const displayed = noMatch ? myBaskets : filtered;

  const hardOpticText = OPTIC_FACTS.find((f) => f.id === hardId)?.text;

  const handleMark = (fid) => {
    const fact = OPTIC_FACTS.find((f) => f.id === fid);
    if (onHardMarked) {
      onHardMarked({
        hard_id: fid,
        hard_text: fact?.text,
        basket_name: fact?.basket ?? detailKeyword(fact?.text),
      });
    }
    setHardId(fid);
    setSearchVal(fact?.basket ?? detailKeyword(fact?.text));
    setNewBasketMode(false);
    setTimeout(() => searchRef.current?.focus(), 120);
  };

  const handleConfirm = (name) => {
    setOpticBasket(name);
    setConfirmed(true);
  };

  return (
    <div>
      {confirmed ? (
        <div style={{ textAlign: 'center', marginBottom: 16 }}>
          <div style={{ fontSize: 10, color: '#999', fontFamily: VD, textTransform: 'uppercase', letterSpacing: 2, marginBottom: 4 }}>picture this</div>
          <div style={{ fontSize: 22, fontWeight: 'bold', color: '#111', fontFamily: VD }}>5 months later</div>
        </div>
      ) : (
        <p style={{ fontSize: 13, color: '#666', textAlign: 'center', marginBottom: 14, fontFamily: VD, lineHeight: 1.6 }}>
          {!hardId ? (
            <>
              You're now studying <strong>Optic Neuritis</strong>. Notice Beta Blockers is already on the right. Mark what's hard for you.
            </>
          ) : (
            <>Search your baskets. No match? Create a new one.</>
          )}
        </p>
      )}

      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', marginBottom: 10 }}>
        <div style={{ flex: '0 0 46%', display: 'flex', flexDirection: 'column', gap: 8 }}>
          <ColLabel>New item</ColLabel>
          {!confirmed ? (
            <TopicCard title="Optic Neuritis" subject="Ophthalmology" color={C.pastel.lavender} facts={OPTIC_FACTS} hardId={hardId} onMark={!hardId ? handleMark : null} />
          ) : (
            <div style={{ border: C.border, borderRadius: 10, overflow: 'hidden', background: '#fff', boxShadow: '2px 2px 0 #111' }}>
              <div style={{ background: C.pastel.yellow, borderBottom: C.border, padding: '8px 10px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span style={{ fontFamily: VD, fontWeight: 'bold', fontSize: 12, color: '#888', fontStyle: 'italic' }}>Your 804th topic</span>
                <Pill style={{ fontSize: 9, background: C.pastel.purple, border: '1.5px solid #ccc', color: '#666' }}>Last subject</Pill>
              </div>
              {[
                { text: 'Fact about this topic', hard: false },
                { text: 'Another fact about this topic', hard: false },
                { text: 'One more fact about this topic', hard: false },
                { text: 'This is the most high yield for me', hard: true },
              ].map((f, i) => (
                <div
                  key={i}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '7px 10px',
                    borderBottom: i < 3 ? C.thin : 'none',
                    background: f.hard ? C.pastel.orange : '#fff',
                  }}
                >
                  <span style={{ fontSize: 11, fontFamily: VD, color: f.hard ? '#333' : '#bbb', flex: 1, paddingRight: 6, fontWeight: f.hard ? 'bold' : 'normal', fontStyle: !f.hard ? 'italic' : 'normal' }}>
                    {f.text}
                  </span>
                  {f.hard ? <HardBadge /> : <MarkAsHardBtn dimmed />}
                </div>
              ))}
            </div>
          )}

          <div
            style={{
              opacity: hardId && !confirmed ? 1 : 0,
              transform: hardId && !confirmed ? 'translateY(0)' : 'translateY(4px)',
              transition: 'opacity .3s, transform .3s',
              pointerEvents: hardId && !confirmed ? 'auto' : 'none',
              display: 'flex',
              flexDirection: 'column',
              gap: 6,
            }}
          >
            <SearchBar
              value={searchVal}
              onChange={(v) => {
                setSearchVal(v);
                setNewBasketMode(false);
              }}
              searchRef={searchRef}
            />

            {noMatch && !newBasketMode && (
              <button
                type="button"
                onClick={() => setNewBasketMode(true)}
                style={{
                  background: C.bg,
                  border: C.border,
                  borderRadius: 7,
                  padding: '7px 10px',
                  fontFamily: VD,
                  fontSize: 11,
                  color: C.primary,
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  textAlign: 'left',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 5,
                }}
              >
                <span>＋</span> Create <strong>&quot;{searchVal}&quot;</strong>
              </button>
            )}

            {!noMatch && !newBasketMode && hardId && (
              <button
                type="button"
                onClick={() => setNewBasketMode(true)}
                style={{
                  background: 'none',
                  border: 'none',
                  padding: '2px 0',
                  fontFamily: VD,
                  fontSize: 10,
                  color: '#bbb',
                  cursor: 'pointer',
                  textAlign: 'left',
                }}
              >
                + or create new basket
              </button>
            )}

            {newBasketMode && (
              <>
                <div style={{ background: C.pastel.blue, border: C.border, borderRadius: 7, padding: '8px 10px', fontSize: 11, fontFamily: VD, color: '#1a237e', lineHeight: 1.5 }}>
                  🧺 New basket <strong>&quot;{searchVal}&quot;</strong> with Optic Neuritis inside.
                </div>
                <BtnPrimary onClick={() => handleConfirm(searchVal)} style={{ fontSize: 12, padding: '9px 12px' }}>
                  Create &amp; add →
                </BtnPrimary>
              </>
            )}
          </div>

          {/*
            Confirmed (\"5 months later\") state is meant to be a static preview.
            Remove the extra search bar to reduce visual noise.
          */}
        </div>

        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8, minWidth: 0 }}>
          <ColLabel>Your baskets</ColLabel>
          {!confirmed ? (
            displayed.map((b) => (
              <div key={b.id} onClick={() => (hardId && !newBasketMode ? handleConfirm(b.name) : undefined)} style={{ cursor: hardId && !newBasketMode ? 'pointer' : 'default' }}>
                <BasketCard name={b.name} topics={b.topics} />
              </div>
            ))
          ) : (
            <>
              {hardOpticText && opticBasket && (
                <BasketCard name={opticBasket} topics={[{ name: 'Optic Neuritis', detail: hardOpticText, color: C.pastel.lavender, tag: 'Ophthalmology' }]} badge="new ✓" selected />
              )}
              <BasketCard name={basketName} topics={betaTopics} />

              {[
                { w1: '60%', rows: [['55%', '75%'], ['40%', '65%']] },
                { w1: '70%', rows: [['65%', '80%']] },
              ].map((sk, i) => (
                <div key={i} style={{ border: '1.5px solid #e8e8e8', borderRadius: 9, overflow: 'hidden', background: '#fafafa' }}>
                  <div style={{ background: '#f2f2f2', borderBottom: '1px solid #e8e8e8', padding: '7px 9px', display: 'flex', alignItems: 'center', gap: 6 }}>
                    <div style={{ width: 12, height: 12, borderRadius: 3, background: '#ddd', flexShrink: 0 }} />
                    <div style={{ height: 9, borderRadius: 3, background: '#ddd', width: sk.w1 }} />
                  </div>
                  {sk.rows.map((pair, ri) => (
                    <div key={ri} style={{ padding: '7px 9px', borderBottom: ri < sk.rows.length - 1 ? '1px solid #ebebeb' : 'none', display: 'flex', flexDirection: 'column', gap: 4 }}>
                      <div style={{ height: 8, borderRadius: 3, background: '#e8e8e8', width: pair[0] }} />
                      <div style={{ height: 7, borderRadius: 3, background: '#efefef', width: pair[1] }} />
                    </div>
                  ))}
                </div>
              ))}

              <div style={{ textAlign: 'center', fontSize: 10, color: '#bbb', fontFamily: VD, paddingTop: 2 }}>60+ baskets</div>
            </>
          )}
        </div>
      </div>

      {hardId && !confirmed && (
        <div style={{ background: C.pastel.yellow, border: C.border, borderRadius: 8, padding: '9px 12px', fontSize: 11, fontFamily: VD, color: '#5d4037', lineHeight: 1.6 }}>
          Don't feel pressured to find the perfect basket. You'll build more as you go. What matters is that every item has a home.
        </div>
      )}

      {confirmed && (
        <div style={{ marginTop: 10 }}>
          <div style={{ background: C.pastel.blue, border: C.border, borderRadius: 8, padding: '9px 12px', fontSize: 11, fontFamily: VD, color: '#1a237e', lineHeight: 1.6, marginBottom: 10 }}>
            Even as you move to new subjects, older items stay on the right, keeping their hard details fresh in your memory.
          </div>
          <BtnPrimary onClick={() => onNext({ opticBasket, hardOpticText })}>Why does this work? →</BtnPrimary>
        </div>
      )}
    </div>
  );
};

const Step5Congrats = ({ hardText, basketName, hardOpticText, onNext }) => {
  const items = [
    {
      title: 'You targeted what’s hard for you',
      body: `You marked "${hardText}" in Beta Blockers and "${hardOpticText || 'a detail'}" in Optic Neuritis, prioritising the details that are hard or high-yield for you, not everything.`,
      color: C.pastel.yellow,
    },
    {
      title: 'Old items revised themselves',
      body: `To basket Optic Neuritis, you scanned your Beta Blockers basket. That scan quietly re-exposed you to a hard detail from an earlier topic. No separate revision session needed.`,
      color: C.pastel.blue,
    },
  ];

  return (
    <div>
      <div style={{ textAlign: 'center', marginBottom: 18 }}>
        <div style={{ fontSize: 40, marginBottom: 8 }}>🎉</div>
        <h2 style={{ fontSize: 20, fontWeight: 'bold', color: '#111', fontFamily: VD, marginBottom: 6 }}>Here's what you just did</h2>
        <p style={{ fontSize: 13, color: '#666', fontFamily: VD, lineHeight: 1.6 }}>Without trying, you experienced what makes Basket different.</p>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 20 }}>
        {items.map((item, i) => (
          <div key={i} style={{ background: item.color, border: C.border, borderRadius: 10, padding: '14px', cursor: 'default', display: 'flex', gap: 12, alignItems: 'flex-start' }}>
            <div style={{ width: 22, height: 22, borderRadius: 4, flexShrink: 0, marginTop: 1, border: '2px solid #2e7d32', background: '#2e7d32', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all .25s' }}>
              <span style={{ color: '#fff', fontSize: 13, lineHeight: 1 }}>✓</span>
            </div>
            <div>
              <div style={{ fontFamily: VD, fontWeight: 'bold', fontSize: 13, color: '#111', marginBottom: 4 }}>{item.title}</div>
              <div style={{ fontFamily: VD, fontSize: 11, color: '#555', lineHeight: 1.6 }}>{item.body}</div>
            </div>
          </div>
        ))}
      </div>

      <BtnPrimary onClick={onNext}>Show me a strong basket →</BtnPrimary>
    </div>
  );
};

const Step6GrownBasket = ({ onNext }) => {
  const [phase, setPhase] = useState(0);

  return (
    <div>
      {phase === 0 && (
        <>
          <h2 style={{ fontSize: 20, fontWeight: 'bold', textAlign: 'center', marginBottom: 4, color: '#111', fontFamily: VD }}>This is what a basket looks like later</h2>
          <p style={{ fontSize: 13, color: '#666', textAlign: 'center', marginBottom: 14, fontFamily: VD, lineHeight: 1.6 }}>
            Someone started with one hard detail, just like you. Over time, topics from different subjects connected to that same detail and ended up in the same basket.
          </p>

          <BasketCard name="terminal ileum" topics={TERMINAL_ILEUM_BASKET.topics} style={{ marginBottom: 12 }} />

          <div style={{ background: C.pastel.green, border: C.border, borderRadius: 8, padding: '11px 14px', fontSize: 12, fontFamily: VD, color: '#1b5e20', lineHeight: 1.7, marginBottom: 16 }}>
            Three topics. Three subjects. One basket name. All can be recalled as a single unit, even under exam pressure.
          </div>
          <BtnPrimary onClick={() => setPhase(1)}>What makes this basket strong? →</BtnPrimary>
        </>
      )}

      {phase === 1 && (
        <>
          <h2 style={{ fontSize: 20, fontWeight: 'bold', textAlign: 'center', marginBottom: 14, color: '#111', fontFamily: VD }}>A strong basket</h2>

          <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', marginBottom: 16 }}>
            <div style={{ flex: '0 0 46%', display: 'flex', flexDirection: 'column', gap: 8, minWidth: 0 }}>
              {[
                { title: 'Multiple subjects', body: 'Grouping topics from multiple subjects as one unit compresses the volume you need to hold in memory.' },
                { title: 'Hard detail of each item is marked', body: 'Each topic is here because of one detail connecting to the basket name or another item inside.' },
                { title: 'Recalled as a unit', body: 'Start from any topic. The others follow.' },
              ].map((item, i) => (
                <div key={i} style={{ background: '#fff', border: C.border, borderRadius: 8, padding: '9px 10px', display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                  <div style={{ width: 18, height: 18, borderRadius: 4, flexShrink: 0, marginTop: 1, background: '#2e7d32', border: '2px solid #2e7d32', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <span style={{ color: '#fff', fontSize: 11, lineHeight: 1 }}>✓</span>
                  </div>
                  <div>
                    <div style={{ fontFamily: VD, fontWeight: 'bold', fontSize: 10, color: '#111', marginBottom: 2 }}>{item.title}</div>
                    <div style={{ fontFamily: VD, fontSize: 10, color: '#555', lineHeight: 1.5 }}>{item.body}</div>
                  </div>
                </div>
              ))}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <BasketCard name="terminal ileum" topics={TERMINAL_ILEUM_BASKET.topics} />
            </div>
          </div>

          <BtnPrimary onClick={onNext}>Let's try the study mode →</BtnPrimary>
        </>
      )}
    </div>
  );
};

const Step7Done = ({ basketName, hardText, hardOpticText, opticBasket, onImport, onSkip, isImporting }) => {
  const [studyMode, setStudyMode] = useState(true);
  const [revealed, setRevealed] = useState({});

  const toggle = (key) => setRevealed((prev) => ({ ...prev, [key]: !prev[key] }));

  const betaTopics = [{ name: 'Beta Blockers', detail: hardText, color: C.pastel.teal, tag: 'Pharmacology' }];
  const opticTopics = [{ name: 'Optic Neuritis', detail: hardOpticText, color: C.pastel.lavender, tag: 'Ophthalmology' }];

  const allBaskets = [
    { id: 'beta', name: basketName, topics: betaTopics },
    opticBasket ? { id: 'optic', name: opticBasket, topics: opticTopics } : null,
    { id: 'ti', name: 'terminal ileum', topics: TERMINAL_ILEUM_BASKET.topics, badge: 'example' },
  ].filter(Boolean);

  return (
    <div>
      <div style={{ textAlign: 'center', marginBottom: 14 }}>
        <div style={{ fontSize: 40, marginBottom: 8 }}>🧺</div>
        <h2 style={{ fontSize: 21, fontWeight: 'bold', color: '#111', fontFamily: VD, marginBottom: 4 }}>This is study mode</h2>
        <p style={{ fontSize: 12, color: '#666', fontFamily: VD, lineHeight: 1.6 }}>
          Items are hidden. Try to recall what's in each basket before revealing. Ask yourself: <em>What&apos;s inside? Why is it there?</em>
        </p>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: C.bg, border: C.border, borderRadius: 8, padding: '10px 14px', marginBottom: 14 }}>
        <div>
          <div style={{ fontFamily: VD, fontWeight: 'bold', fontSize: 12, color: '#111' }}>Study mode</div>
          <div style={{ fontFamily: VD, fontSize: 10, color: '#888', marginTop: 1 }}>Items hidden. Tap any basket row to reveal</div>
        </div>
        <button
          type="button"
          onClick={() => {
            setStudyMode((s) => !s);
            setRevealed({});
          }}
          aria-label="Toggle study mode"
          style={{ width: 44, height: 24, borderRadius: 12, cursor: 'pointer', background: studyMode ? C.primary : '#ddd', border: C.border, position: 'relative', transition: 'background .2s' }}
        >
          <div style={{ width: 18, height: 18, borderRadius: '50%', background: '#fff', border: '2px solid #111', position: 'absolute', top: 1, transition: 'left .2s', left: studyMode ? 22 : 2 }} />
        </button>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 20 }}>
        {allBaskets.map((b) => (
          <div key={b.id} style={{ border: C.border, borderRadius: 9, overflow: 'hidden', background: '#fff', boxShadow: '2px 2px 0 #111' }}>
            <div style={{ background: '#fafafa', borderBottom: C.thin, padding: '7px 9px', display: 'flex', alignItems: 'center', gap: 5 }}>
              <span style={{ fontSize: 13 }}>🧺</span>
              <span style={{ fontFamily: VD, fontWeight: 'bold', fontSize: 12, color: '#444', flex: 1 }}>{b.name}</span>
              {b.badge && <Pill bg={C.pastel.green} color="#2e7d32" style={{ fontSize: 9 }}>{b.badge}</Pill>}
              <span style={{ fontSize: 10, color: '#bbb' }}>{b.topics.length}t</span>
            </div>
            {b.topics.map((t, i) => {
              const key = `${b.id}-${i}`;
              const isRevealed = revealed[key];
              return (
                <div
                  key={key}
                  onClick={() => (studyMode ? toggle(key) : undefined)}
                  style={{ background: '#fff', borderBottom: i < b.topics.length - 1 ? C.thin : 'none', padding: '8px 9px', cursor: studyMode ? 'pointer' : 'default' }}
                >
                  {studyMode && !isRevealed ? (
                    <div style={{ background: '#f5f5f5', color: '#bbb', border: '1px dashed #ccc', borderRadius: 5, padding: '6px 10px', fontFamily: VD, fontSize: 10, textAlign: 'center', userSelect: 'none', fontStyle: 'italic' }}>
                      tap to reveal ↓
                    </div>
                  ) : (
                    <>
                      <div style={{ display: 'inline-block', background: t.color, borderRadius: 5, padding: '2px 8px', fontWeight: '700', fontSize: 11, color: '#111', fontFamily: VD, marginBottom: 4, maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {t.name}
                      </div>
                      {t.reason && <div style={{ fontSize: 9, color: C.primary, fontFamily: VD, fontStyle: 'italic', opacity: 0.85, marginBottom: 2 }}>🔗 {t.reason}</div>}
                      <div style={{ fontSize: 10, color: '#666', fontFamily: VD, lineHeight: 1.4 }}>{t.detail}</div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        ))}
      </div>

      <BtnPrimary onClick={onImport} disabled={isImporting} style={{ marginBottom: 10, fontSize: 14 }}>
        {isImporting ? 'Importing…' : '🧺 Import Workspace (FREE)'}
      </BtnPrimary>
      <button
        type="button"
        onClick={onSkip}
        disabled={isImporting}
        style={{ width: '100%', background: 'none', border: 'none', fontFamily: VD, fontSize: 12, color: '#bbb', cursor: isImporting ? 'not-allowed' : 'pointer', padding: '8px 0', textAlign: 'center' }}
      >
        Skip and go to home
      </button>
    </div>
  );
};

const Progress = ({ step, total }) => {
  return (
    <div style={{ padding: '10px 20px 0', display: 'flex', alignItems: 'center', gap: 8 }}>
      <div style={{ flex: 1, height: 3, background: '#eee', borderRadius: 3, overflow: 'hidden', border: '1px solid #ddd' }}>
        <div style={{ width: `${((step + 1) / total) * 100}%`, height: '100%', background: C.primary, transition: 'width .4s ease' }} />
      </div>
      <span style={{ fontSize: 11, color: '#bbb', fontFamily: VD, whiteSpace: 'nowrap' }}>
        {step + 1} / {total}
      </span>
    </div>
  );
};

const OnboardingTour = ({ isOpen, onClose, userId }) => {
  const [step, setStep] = useState(0);
  const [hardText, setHardText] = useState('');
  const [basketName, setBasketName] = useState('');
  const [hardOpticText, setHardOpticText] = useState('');
  const [opticBasket, setOpticBasket] = useState('');
  const [showExitModal, setShowExitModal] = useState(false);
  const [hasCompletedTour, setHasCompletedTour] = useState(false);
  const [tourId, setTourId] = useState(null);
  const [isImporting, setIsImporting] = useState(false);

  const scrollRef = useRef(null);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const TOTAL = 7;

  const getStepContext = (s) => ({
    step_index: s,
    step_name: STEP_NAMES[s] || `step_${s}`,
  });

  const resetState = () => {
    setStep(0);
    setHardText('');
    setBasketName('');
    setHardOpticText('');
    setOpticBasket('');
    setShowExitModal(false);
    setHasCompletedTour(false);
    setTourId(null);
    setIsImporting(false);
  };

  useEffect(() => {
    if (!isOpen) {
      resetState();
      return;
    }
    captureOnboardingEvent('onboarding_v2_opened', {
      ...getStepContext(step),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  useEffect(() => {
    const initializeTour = async () => {
      if (!userId || !isOpen) return;
      try {
        const { data: newTour, error } = await supabase
          .from('onboarding_tours')
          .insert([{ user_id: userId, started_at: new Date().toISOString(), last_step_completed: 0 }])
          .select()
          .single();
        if (error) throw error;
        setTourId(newTour.id);
      } catch (e) {
        console.error('Error initializing tour:', e);
      }
    };
    initializeTour();
  }, [userId, isOpen]);

  useEffect(() => {
    const fetchCompletionStatus = async () => {
      if (!userId || !isOpen) return;
      try {
        const { data, error } = await supabase.from('user_profiles').select('onboarding_tour_completed').eq('id', userId).single();
        if (error) throw error;
        setHasCompletedTour(Boolean(data?.onboarding_tour_completed));
      } catch (e) {
        // If we can't confirm completion status, default to treating them as not completed
        // so the user doesn't accidentally lose progress without a confirm prompt.
        setHasCompletedTour(false);
      }
    };
    fetchCompletionStatus();
  }, [userId, isOpen]);

  const advance = (data = {}) => {
    if (data.hardText) setHardText(data.hardText);
    if (data.basketName) setBasketName(data.basketName);
    if (data.hardOpticText) setHardOpticText(data.hardOpticText);
    if (data.opticBasket) setOpticBasket(data.opticBasket);
    setStep((s) => s + 1);
    setTimeout(() => scrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' }), 50);
  };

  useEffect(() => {
    if (!isOpen) return;
    captureOnboardingEvent('onboarding_v2_step_viewed', {
      ...getStepContext(step),
    });
  }, [isOpen, step]);

  const hasMeaningfulProgress = useMemo(() => {
    return Boolean(hardText || basketName || hardOpticText || opticBasket || step > 0);
  }, [hardText, basketName, hardOpticText, opticBasket, step]);

  const handleClose = () => {
    // If the user has already completed onboarding previously, allow closing anywhere without confirmation.
    if (hasCompletedTour) {
      captureOnboardingEvent('onboarding_v2_closed', {
        ...getStepContext(step),
        reason: 'closed_previously_completed_user',
      });
      onClose();
      return;
    }

    // Otherwise, confirm exiting on every step except the final "completed" screen.
    if (step === TOTAL - 1) {
      captureOnboardingEvent('onboarding_v2_closed', {
        ...getStepContext(step),
        reason: 'closed_on_final_step',
      });
      // Mark completed the same way as handleConfirmedExit before closing.
      (async () => {
        try {
          if (userId) {
            const { error: profileError } = await supabase
              .from('user_profiles')
              .update({ onboarding_tour_completed: true, updated_at: new Date().toISOString() })
              .eq('id', userId);
            if (profileError) console.error('Error marking tour completed on close:', profileError);
            else syncBrevoContact();
          }
          if (tourId) {
            await supabase
              .from('onboarding_tours')
              .update({ completed_at: new Date().toISOString() })
              .eq('id', tourId);
          }
        } catch (e) {
          console.error('Error marking tour completed on close:', e);
        } finally {
          onClose();
        }
      })();
      return;
    }

    captureOnboardingEvent('onboarding_v2_close_clicked', {
      ...getStepContext(step),
      had_meaningful_progress: hasMeaningfulProgress,
    });
    setShowExitModal(true);
  };

  const handleConfirmedExit = async () => {
    captureOnboardingEvent('onboarding_v2_exit_confirmed', {
      ...getStepContext(step),
    });
    try {
      // If they reached the final step, treat exiting as "completed" so it won't auto-open again.
      // (They can still reopen manually from the profile menu.)
      if (userId && step === TOTAL - 1) {
        const { error: profileError } = await supabase
          .from('user_profiles')
          .update({ onboarding_tour_completed: true, updated_at: new Date().toISOString() })
          .eq('id', userId);
        if (profileError) console.error('Error exiting tour:', profileError);
        else syncBrevoContact();
      }

      if (tourId) {
        if (step === TOTAL - 1) {
          await supabase
            .from('onboarding_tours')
            .update({ completed_at: new Date().toISOString() })
            .eq('id', tourId);
        } else {
          await supabase.from('onboarding_tours').delete().eq('id', tourId);
        }
      }
    } catch (e) {
      console.error('Error exiting tour:', e);
    } finally {
      setShowExitModal(false);
      onClose();
    }
  };

  const handleEnsureWorkspace = async () => {
    // Onboarding import should always create a brand-new workspace.
    const { data: newWorkspace, error: createError } = await supabase
      .from('workspaces')
      .insert([{ user_id: userId, name: 'My workspace' }])
      .select('id, name')
      .single();
    if (createError) throw createError;
    return newWorkspace;
  };

  const handleGetUniqueName = async ({ workspaceId, desiredName, fallbackBase }) => {
    const base = (desiredName || fallbackBase || 'basket').trim();
    const candidateBase = base || 'basket';

    const { data: existing, error } = await supabase
      .from('baskets')
      .select('name')
      .eq('workspace_id', workspaceId)
      .in('name', [candidateBase, `${candidateBase} (onboarding)`, `${candidateBase} (onboarding 2)`, `${candidateBase} (onboarding 3)`]);

    if (error) {
      // If we can't check, just return the desired name and let insert surface a useful error.
      return candidateBase;
    }

    const existingNames = new Set((existing || []).map((b) => b.name));
    if (!existingNames.has(candidateBase)) return candidateBase;
    if (!existingNames.has(`${candidateBase} (onboarding)`)) return `${candidateBase} (onboarding)`;
    if (!existingNames.has(`${candidateBase} (onboarding 2)`)) return `${candidateBase} (onboarding 2)`;
    if (!existingNames.has(`${candidateBase} (onboarding 3)`)) return `${candidateBase} (onboarding 3)`;
    return `${candidateBase} (onboarding ${Date.now()})`;
  };

  const handleEnsureTags = async ({ workspaceId, tagSpecs }) => {
    const names = tagSpecs.map((t) => t.name);
    const { data: existing, error: existingError } = await supabase.from('tags').select('id, name').eq('workspace_id', workspaceId).in('name', names);
    if (existingError) throw existingError;

    const existingByName = new Map((existing || []).map((t) => [t.name, t]));
    const missing = tagSpecs.filter((t) => !existingByName.has(t.name)).map((t) => ({ ...t, workspace_id: workspaceId }));

    let inserted = [];
    if (missing.length > 0) {
      const { data: insertedTags, error: insertError } = await supabase.from('tags').insert(missing).select('id, name');
      if (insertError) throw insertError;
      inserted = insertedTags || [];
    }

    const all = [...(existing || []), ...inserted];
    return new Map(all.map((t) => [t.name, t.id]));
  };

  const handleImportWorkspace = async () => {
    if (isImporting) return;
    if (!userId) {
      toast.error('You must be signed in to import a workspace.');
      return;
    }
    if (!basketName || !hardText) {
      toast.error('Finish the tutorial steps first.');
      return;
    }

    captureOnboardingEvent('onboarding_v2_workspace_import_clicked', {
      ...getStepContext(step),
    });

    setIsImporting(true);
    try {
      const workspace = await handleEnsureWorkspace();
      const workspaceId = workspace.id;

      const desiredOpticBasketName = opticBasket && opticBasket.trim() ? opticBasket : 'optic neuritis';
      const safeBetaBasketName = await handleGetUniqueName({ workspaceId, desiredName: basketName, fallbackBase: 'beta blockers' });
      const safeOpticBasketName = await handleGetUniqueName({ workspaceId, desiredName: desiredOpticBasketName, fallbackBase: 'optic neuritis' });
      // Terminal ileum should exist only once per workspace.
      // If it already exists, we reuse it; otherwise we create exactly `terminal ileum` (no onboarding suffix).
      const { data: existingTerminalIleum, error: existingTerminalIleumError } = await supabase
        .from('baskets')
        .select('id, name')
        .eq('workspace_id', workspaceId)
        .eq('name', 'terminal ileum')
        .limit(1);
      if (existingTerminalIleumError) throw existingTerminalIleumError;
      const terminalIleumBasketExisting = existingTerminalIleum && existingTerminalIleum.length > 0 ? existingTerminalIleum[0] : null;

      // Ensure the three basket names are distinct (we guarantee 3 baskets for onboarding import).
      const distinctNames = new Set([safeBetaBasketName, safeOpticBasketName]);
      if (distinctNames.size !== 2) {
        const forcedOptic = await handleGetUniqueName({ workspaceId, desiredName: 'optic neuritis', fallbackBase: 'optic neuritis' });
        distinctNames.clear();
        distinctNames.add(safeBetaBasketName);
        distinctNames.add(forcedOptic);
      }

      const [finalBetaName, finalOpticName] = Array.from(distinctNames);
      const finalTIName = 'terminal ileum';

      const basketsToCreate = [
        {
          workspace_id: workspaceId,
          name: finalBetaName,
          description: `topics linked to ${finalBetaName}`,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        },
        {
          workspace_id: workspaceId,
          name: finalOpticName,
          description: `topics linked to ${finalOpticName}`,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        },
        ...(terminalIleumBasketExisting
          ? []
          : [
              {
                workspace_id: workspaceId,
                name: finalTIName,
                description: `topics linked to ${finalTIName}`,
                created_at: new Date().toISOString(),
                updated_at: new Date().toISOString(),
              },
            ]),
      ];

      const { data: baskets, error: basketError } = await supabase.from('baskets').insert(basketsToCreate).select('id, name');
      if (basketError) throw basketError;

      const basketByName = new Map(baskets.map((b) => [b.name, b]));
      const betaBasket = basketByName.get(finalBetaName);
      const opticBasketRow = basketByName.get(finalOpticName);
      const terminalIleumBasket = terminalIleumBasketExisting || basketByName.get(finalTIName);

      if (!betaBasket || !opticBasketRow || !terminalIleumBasket) throw new Error('Failed to create required baskets.');

      const tagSpecs = [
        { name: 'Pharmacology', color: '#fef3c7' },
        { name: 'Ophthalmology', color: '#ede7f6' },
        { name: 'Gastroenterology', color: '#dbeafe' },
        { name: 'Cardiology', color: '#fee2e2' },
        { name: 'Genetics', color: '#f3e8ff' },
      ];

      const tagIdByName = await handleEnsureTags({ workspaceId, tagSpecs });

      const { data: existingTerminalTopics, error: existingTerminalTopicsError } = await supabase
        .from('topics')
        .select('id')
        .eq('workspace_id', workspaceId)
        .eq('basket_id', terminalIleumBasket.id)
        .limit(1);
      if (existingTerminalTopicsError) throw existingTerminalTopicsError;
      const terminalIleumAlreadyHasTopics = existingTerminalTopics && existingTerminalTopics.length > 0;

      const opticConceptTextForImport =
        (hardOpticText || '').trim() ||
        OPTIC_FACTS.find((f) => f.id === 'pain')?.text ||
        'Painful acute vision loss';

      const betaRelationDescription =
        getBetaHardcodedRelationDescription(hardText) || formatRelationDescriptionBeta({ topicName: 'Beta Blockers', basketName: finalBetaName });

      const opticRelationDescription =
        getOpticHardcodedRelationDescription(opticConceptTextForImport) || formatRelationDescriptionOptic({ topicName: 'Optic Neuritis', basketName: finalOpticName });

      const topicsToInsert = [
        {
          name: 'Beta Blockers',
          relation_description: betaRelationDescription,
          basket_id: betaBasket.id,
          user_id: userId,
          workspace_id: workspaceId,
          order_index: 0,
          tag_id: tagIdByName.get('Pharmacology'),
        },
        {
          name: 'Optic Neuritis',
          relation_description: opticRelationDescription,
          basket_id: opticBasketRow.id,
          user_id: userId,
          workspace_id: workspaceId,
          order_index: 0,
          tag_id: tagIdByName.get('Ophthalmology'),
        },
        ...(terminalIleumAlreadyHasTopics
          ? []
          : TERMINAL_ILEUM_BASKET.topics.map((t, idx) => ({
              name: t.name,
              relation_description: getTerminalIleumHardcodedRelationDescription(t.name) || t.reason || 'linked to basket name',
              basket_id: terminalIleumBasket.id,
              user_id: userId,
              workspace_id: workspaceId,
              order_index: idx,
              tag_id: tagIdByName.get(t.tag),
            }))),
      ];

      const { data: insertedTopics, error: topicsError } = await supabase.from('topics').insert(topicsToInsert).select('id, basket_id, order_index').order('basket_id, order_index');
      if (topicsError) throw topicsError;

      const betaTopic = insertedTopics.find((t) => t.basket_id === betaBasket.id && t.order_index === 0);
      const opticTopic = insertedTopics.find((t) => t.basket_id === opticBasketRow.id && t.order_index === 0);
      const terminalTopics = insertedTopics.filter((t) => t.basket_id === terminalIleumBasket.id).sort((a, b) => a.order_index - b.order_index);

      const opticConceptText = opticConceptTextForImport;

      const conceptsToInsert = [
        ...(betaTopic
          ? [
              {
                text: hardText,
                is_highlighted: true,
                topic_id: betaTopic.id,
              },
            ]
          : []),
        ...(opticTopic
          ? [
              {
                text: opticConceptText,
                is_highlighted: true,
                topic_id: opticTopic.id,
              },
            ]
          : []),
        ...(terminalIleumAlreadyHasTopics
          ? []
          : terminalTopics.map((topicRow, idx) => ({
              text: TERMINAL_ILEUM_BASKET.topics[idx]?.detail || '',
              is_highlighted: true,
              topic_id: topicRow.id,
            }))),
      ].filter((c) => c.text);

      const { error: conceptsError } = await supabase.from('concepts').insert(conceptsToInsert);
      if (conceptsError) throw conceptsError;

      // Content exists — import succeeded even if profile/tour completion fails.
      try {
        const { error: profileError } = await supabase
          .from('user_profiles')
          .update({ onboarding_tour_completed: true, updated_at: new Date().toISOString() })
          .eq('id', userId);
        if (profileError) console.error('Error marking onboarding completed after import:', profileError);
        else syncBrevoContact();

        if (tourId) {
          const { error: tourError } = await supabase
            .from('onboarding_tours')
            .update({ completed_at: new Date().toISOString(), added_to_basket: true, basket_name: finalBetaName })
            .eq('id', tourId);
          if (tourError) console.error('Error marking onboarding tour completed after import:', tourError);
        }
      } catch (completionError) {
        console.error('Error marking onboarding completed after import:', completionError);
      }

      // Cleanup: remove mistakenly-created duplicate basket (older onboarding) if present.
      // We only target the exact name requested.
      try {
        const { data: dupBaskets } = await supabase
          .from('baskets')
          .select('id')
          .eq('workspace_id', workspaceId)
          .eq('name', 'terminal ileum onboarding')
          .limit(1);

        const dup = dupBaskets && dupBaskets.length > 0 ? dupBaskets[0] : null;
        if (dup) {
          const { data: dupTopics } = await supabase.from('topics').select('id').eq('basket_id', dup.id);
          const dupTopicIds = (dupTopics || []).map((t) => t.id);
          if (dupTopicIds.length > 0) {
            await supabase.from('concepts').delete().in('topic_id', dupTopicIds);
          }
          await supabase.from('topics').delete().eq('basket_id', dup.id);
          await supabase.from('baskets').delete().eq('id', dup.id);
        }
      } catch (cleanupError) {
        console.error('Cleanup error (terminal ileum onboarding):', cleanupError);
      }

      onClose();
      captureOnboardingEvent('onboarding_v2_workspace_import_succeeded', {
        ...getStepContext(step),
        workspace_id: workspaceId,
        baskets_created: 3,
      });
      captureOnboardingEvent('onboarding_v2_closed', {
        ...getStepContext(step),
        reason: 'import_succeeded',
      });
      dispatch(setWorkspaceLoaded(false));
      navigate(`/workspace/${workspaceId}`, {
        state: {
          workspaceName: workspace.name,
          workspaceOpenSource: 'created',
        },
      });
    } catch (e) {
      console.error('Error importing workspace:', e);
      captureOnboardingEvent('onboarding_v2_workspace_import_failed', {
        ...getStepContext(step),
        error_message: e?.message,
      });
      toast.error(e?.message || 'Failed to import workspace. Please try again.');
    } finally {
      setIsImporting(false);
    }
  };

  const handleSkipFromFinalStep = async () => {
    if (!userId) {
      captureOnboardingEvent('onboarding_v2_skipped_import', {
        ...getStepContext(step),
        reason: 'no_user',
      });
      onClose();
      return;
    }

    captureOnboardingEvent('onboarding_v2_skipped_import', {
      ...getStepContext(step),
    });

    try {
      const { error: profileError } = await supabase
        .from('user_profiles')
        .update({ onboarding_tour_completed: true, updated_at: new Date().toISOString() })
        .eq('id', userId);
      if (profileError) throw profileError;
      syncBrevoContact();

      if (tourId) {
        await supabase
          .from('onboarding_tours')
          .update({ completed_at: new Date().toISOString(), added_to_basket: false })
          .eq('id', tourId);
      }
    } catch (e) {
      console.error('Error marking onboarding as completed on skip:', e);
    } finally {
      captureOnboardingEvent('onboarding_v2_closed', {
        ...getStepContext(step),
        reason: 'skipped_import_final_step',
      });
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-2 z-50" role="dialog" aria-modal="true" aria-label="Basket onboarding tutorial">
        <div style={{ background: '#fff', border: C.border, borderRadius: 16, width: '100%', maxWidth: 480, boxShadow: '4px 4px 0 #111', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          <div style={{ background: C.bg, borderBottom: C.border, padding: '11px 18px', display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 20 }}>🧺</span>
            <span style={{ fontWeight: 'bold', fontSize: 16, color: C.primary, fontFamily: VD }}>Basket</span>
            <button
              type="button"
              onClick={handleClose}
              disabled={isImporting}
              style={{
                marginLeft: 'auto',
                background: 'none',
                border: 'none',
                cursor: isImporting ? 'not-allowed' : 'pointer',
                opacity: isImporting ? 0.5 : 1,
              }}
              aria-label="Close tutorial"
            >
              <XMarkIcon className="h-5 w-5 text-neutral-600" />
            </button>
          </div>

          <Progress step={step} total={TOTAL} />

          <div ref={scrollRef} style={{ padding: '20px 20px 24px', overflowY: 'auto', maxHeight: 'calc(100vh - 140px)', fontFamily: VD }}>
            {step === 0 && (
              <Step1MarkHard
                onHardMarked={(payload) => {
                  captureOnboardingEvent('onboarding_v2_beta_hard_marked', {
                    ...getStepContext(step),
                    ...payload,
                  });
                }}
                onNext={(d) => advance(d)}
              />
            )}
            {step === 1 && <Step2BasketNamed hardText={hardText} basketName={basketName} onNext={() => advance()} />}
            {step === 2 && <Step3BSIPreview basketName={basketName} hardText={hardText} onNext={() => advance()} />}
            {step === 3 && (
              <Step4OpticBasket
                basketName={basketName}
                hardText={hardText}
                onHardMarked={(payload) => {
                  captureOnboardingEvent('onboarding_v2_optic_hard_marked', {
                    ...getStepContext(step),
                    ...payload,
                  });
                }}
                onNext={(d) => advance(d)}
              />
            )}
            {step === 4 && <Step5Congrats hardText={hardText} basketName={basketName} hardOpticText={hardOpticText} onNext={() => advance()} />}
            {step === 5 && <Step6GrownBasket onNext={() => advance()} />}
            {step === 6 && (
              <Step7Done
                basketName={basketName}
                hardText={hardText}
                hardOpticText={hardOpticText}
                opticBasket={opticBasket}
                onImport={handleImportWorkspace}
                onSkip={handleSkipFromFinalStep}
                isImporting={isImporting}
              />
            )}
          </div>
        </div>
      </div>

      {showExitModal && !hasCompletedTour && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-[60]" role="dialog" aria-modal="true" aria-label="Exit tutorial confirmation">
          <div style={{ background: '#fff', border: C.border, borderRadius: 16, width: '100%', maxWidth: 420, boxShadow: '4px 4px 0 #111', padding: 16, fontFamily: VD }}>
            <div style={{ fontSize: 16, fontWeight: 'bold', marginBottom: 8, color: '#111' }}>Exit tutorial?</div>
            <div style={{ fontSize: 12, color: '#666', lineHeight: 1.6 }}>Your progress will be reset if you exit now. Are you sure you want to leave?</div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 16 }}>
              <button type="button" onClick={() => setShowExitModal(false)} style={{ background: 'none', border: 'none', fontFamily: VD, fontSize: 12, color: '#999', cursor: 'pointer', padding: '8px 10px' }}>
                Continue
              </button>
              <button type="button" onClick={handleConfirmedExit} style={{ background: C.primary, color: '#fff', border: C.border, borderRadius: 10, fontFamily: VD, fontWeight: 'bold', fontSize: 12, cursor: 'pointer', padding: '10px 12px' }}>
                Exit tutorial
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default OnboardingTour;